import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, MapPin, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useDepot } from "@/context/DepotContext";
import { LoginsCard } from "@/components/coord/LoginsCard";
import { DepotDetailsDialog } from "@/components/coord/DepotDetailsDialog";

const DepotRow = ({ d, onChanged, onDetails }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.name);
  const [busy, setBusy] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setEditing(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  const remove = () => {
    if (window.confirm(`Delete the ${d.name} depot? This only works once it has no jobs, clients, shifts or logins.`)) run(() => api.deleteDepot(d.id), "Depot deleted");
  };

  return (
    <li className="flex items-center gap-3 px-4 py-3" data-testid={`depot-row-${d.id}`}>
      <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><MapPin className="w-4 h-4" /></span>
      {editing ? (
        <Input value={name} autoFocus onChange={(e) => setName(e.target.value)} data-testid={`depot-rename-input-${d.id}`}
          onKeyDown={(e) => e.key === "Enter" && name.trim() && run(() => api.renameDepot(d.id, name.trim()), "Depot renamed")} className="h-9" />
      ) : (
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate" data-testid={`depot-name-${d.id}`}>{d.name}</p>
          <p className="text-xs text-muted-foreground">{d.job_count} job{d.job_count !== 1 ? "s" : ""} · {d.login_count} depot login{d.login_count !== 1 ? "s" : ""}{[d.abn, d.address, d.phone, d.email].some(Boolean) ? " · own business details" : ""}</p>
        </div>
      )}
      {editing ? (
        <>
          <Button size="icon" variant="ghost" className="rounded-full shrink-0" disabled={busy || !name.trim()} data-testid={`depot-rename-save-${d.id}`}
            onClick={() => run(() => api.renameDepot(d.id, name.trim()), "Depot renamed")}><Check className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="rounded-full shrink-0" onClick={() => { setEditing(false); setName(d.name); }}><X className="w-4 h-4" /></Button>
        </>
      ) : (
        <>
          <Button size="icon" variant="ghost" className="rounded-full shrink-0" title="Business details for PDFs" data-testid={`depot-details-${d.id}`} onClick={() => onDetails(d)}><FileText className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="rounded-full shrink-0" data-testid={`depot-rename-${d.id}`} onClick={() => setEditing(true)}><Pencil className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="rounded-full shrink-0 text-destructive hover:bg-destructive/10" disabled={busy} data-testid={`depot-delete-${d.id}`} onClick={remove}><Trash2 className="w-4 h-4" /></Button>
        </>
      )}
    </li>
  );
};

const DepotsCard = () => {
  const { depots, reload } = useDepot();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [detailsFor, setDetailsFor] = useState(null);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.createDepot(name.trim());
      toast.success(depots.length ? "Depot added" : "First depot added — your existing jobs, clients and shifts were moved into it");
      setName(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add depot"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="depots-card">
      <h3 className="font-display text-lg font-bold">Depots</h3>
      <p className="text-sm text-muted-foreground mb-4">Each depot keeps its own jobs, clients, roster, timesheets and payroll. Your crew is shared by every depot.</p>
      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Geelong" data-testid="depot-name-input" />
        <Button onClick={add} disabled={busy || !name.trim()} data-testid="depot-add-btn" className="rounded-full bg-primary hover:bg-primary/90 text-white shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Add depot
        </Button>
      </div>
      {depots.length ? (
        <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
          {depots.map((d) => <DepotRow key={d.id} d={d} onChanged={reload} onDetails={setDetailsFor} />)}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-4" data-testid="depots-empty">No depots yet. Add one to split your work by location — everything you have now moves into your first depot.</p>
      )}
      <DepotDetailsDialog depot={detailsFor} onClose={() => setDetailsFor(null)} onSaved={reload} />
    </div>
  );
};

export const DepotsTab = () => (
  <div className="fade-up">
    <div className="mb-6">
      <h2 className="font-display text-2xl font-bold">Depots & logins</h2>
      <p className="text-sm text-muted-foreground">Set up your locations and give each depot its own supervisor login.</p>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <DepotsCard />
      <LoginsCard />
    </div>
  </div>
);
